"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import successStoriesData from "@/data/success-stories.json";

export default function SuccessStories() {
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const containerRef = useRef(null);
  
  // Flatten the data
  const allLearners = successStoriesData.flat();
  const totalLearners = allLearners.length;

  // Update visible card count based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1536) setVisibleCount(6);
      else if (window.innerWidth >= 1280) setVisibleCount(5);
      else if (window.innerWidth >= 1024) setVisibleCount(4);
      else if (window.innerWidth >= 768) setVisibleCount(3);
      else if (window.innerWidth >= 640) setVisibleCount(2);
      else setVisibleCount(1);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePrev = () => {
    setStartIndex(prev => 
      prev === 0 ? totalLearners - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setStartIndex(prev => 
      prev === totalLearners - 1 ? 0 : prev + 1
    );
  };

  // Get current visible learners
  const visibleLearners = [];
  for (let i = 0; i < visibleCount; i++) {
    const index = (startIndex + i) % totalLearners;
    visibleLearners.push(allLearners[index]);
  }

  return (
    <section className="py-8 md:py-16 w-full">
      <div className="w-[90%] md:w-[80%] mx-auto mb-10">
        <p className="text-[#002726] text-base font-medium uppercase mb-2">SUCCESS STORIES</p>
        <div className="flex flex-col md:flex-row justify-end md:justify-between items-end md:items-center">
          <h2 className="text-[#002726] text-[24px] md:text-4xl font-medium">Our Learners at Leading Companies</h2>
          
          <div className="flex space-x-4">
            <button 
              onClick={handlePrev}
              className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center"
              aria-label="Previous"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="#012726" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button 
              onClick={handleNext}
              className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center"
              aria-label="Next"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="#012726" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="w-full overflow-hidden" ref={containerRef}>
        <div 
          className="flex transition-transform duration-500 ease-in-out pl-[10%]"
          style={{
            transform: `translateX(-${startIndex * (245 + 24)}px)`,
          }}
        >
          {allLearners.map((learner, index) => (
            <div 
              key={`learner-${index}`} 
              className="flex-shrink-0 mr-6" 
            >
              <div className="w-[245px] h-[320px] rounded-2xl border border-[#97a7a6] overflow-hidden">
                <div className="p-4 h-full flex flex-col justify-between">
                  {/* Profile & Name */}
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                      <Image
                        src={learner.image || "https://via.placeholder.com/48"}
                        alt={learner.name}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <h3 className="text-lg font-medium text-[#012726] leading-tight">{learner.name}</h3>
                  </div>
                  
                  {/* Current Role */}
                  <div className="bg-[#f8f8f8] rounded-xl p-4 h-[90px] flex items-center">
                    <div className="w-full">
                      <p className="text-[#012726] text-sm leading-tight font-medium mb-1">{learner.currentRole.title}</p>
                      <p className="text-[#012726] text-sm leading-tight font-semibold">{learner.currentRole.company}</p>
                    </div>
                  </div>
                  
                  {/* Down Arrow - Perfectly centered */}
                  <div className="flex justify-center items-center">
                    <div className="w-9 h-6 flex items-center justify-center">
                       <Image
                         className="w-full h-full object-contain"
                         src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/sssvg.svg"
                         alt="arrow"
                         width={36}
                         height={24}
                         priority
                       />
                    </div>
                  </div>
                  
                  {/* Previous Role */}
                  <div className="bg-[#f8f8f8] rounded-xl p-4 h-[90px] flex items-center">
                    <div className="w-full">
                      <p className="text-[#012726] text-sm leading-tight font-medium mb-1">{learner.previousRole.title}</p>
                      <p className="text-[#012726] text-sm leading-tight font-semibold">{learner.previousRole.company}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}